import { observer } from "mobx-react-lite";
import React, { useContext, useEffect } from "react";
import { StoreContext } from "../stores/rootStore";
import { NodePartNetworking } from "../apiResponses/influxResponses";
import { access } from "fs";
import { DataTable, DataTableSortStatus } from "mantine-datatable";
import { min } from "moment";
import { Stack } from "@mantine/core";
import TimeRangeSelector from "../components/TimeRangeSelector";

const PartNetworking: React.FC = observer(() => {
  const rs = useContext(StoreContext);
  const is = rs.influxStore;

  const [sortStatus, setSortStatus] = React.useState<
    DataTableSortStatus<ProcessedPartNetworkingData>
  >({
    columnAccessor: "node",
    direction: "asc",
  });

  const partNetworkingData = is.nodePartNetworkingReq.getData();
  if (partNetworkingData === null) {
    return <React.Fragment>fetching data</React.Fragment>;
  }

  const cols: any[] = [
    {
      accessor: "node",
      title: "Node",
      sortable: true,
      render: (record: ProcessedPartNetworkingData) => {
        if (record.node_restarted_in_period) {
          return <span style={{ color: "red" }}>{record.node}</span>;
        } else {
          return record.node;
        }
      },
      width: 80,
    },
    {
      accessor: "live_count",
      title: "Live Count",
      sortable: true,
    },
    {
      accessor: (record: ProcessedPartNetworkingData) =>
        record.live_bytes + record.regular_bytes,
      title: "Total Bytes",
      render: (record: ProcessedPartNetworkingData) => {
        return formatBytes(record.live_bytes + record.regular_bytes);
      },
    },
    {
      accessor: "live_bytes",
      title: "Live Bytes",
      sortable: true,
      render: (record: ProcessedPartNetworkingData) => {
        return formatBytes(record.live_bytes);
      },
    },
    {
      accessor: "live_avg_dur",
      title: "Live Avg Dur",
      sortable: true,
    },
    {
      accessor: "regular_count",
      title: "Regular Count",
      sortable: true,
    },
    {
      accessor: "regular_bytes",
      title: "Regular Bytes",
      sortable: true,
      render: (record: ProcessedPartNetworkingData) => {
        return formatBytes(record.regular_bytes);
      },
    },
    {
      accessor: "regular_small_part_avg_dur",
      title: "Regular Small Part Avg Dur",
      sortable: true,
    },
    {
      accessor: "regular_large_part_avg_dur",
      title: "Regular Large Part Avg Dur",
      sortable: true,
    },
  ];

  const processedData = processPartNetworkingData(partNetworkingData);

  processedData.sort((a, b) => {
    const col = sortStatus.columnAccessor as keyof ProcessedPartNetworkingData;
    const direction = sortStatus.direction === "asc" ? 1 : -1;
    if (a[col] < b[col]) {
      return -1 * direction;
    } else if (a[col] > b[col]) {
      return 1 * direction;
    } else {
      return 0;
    }
  });

  return (
    <Stack>
      <TimeRangeSelector />
      <DataTable
        columns={cols}
        records={processedData}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        pinFirstColumn
      />
    </Stack>
  );
});

function processPartNetworkingData(
  data: NodePartNetworking | null,
): ProcessedPartNetworkingData[] {
  if (data === null) {
    return [];
  }
  const result = [];

  for (const [node, partNetworking] of Object.entries(data)) {
    result.push({
      node,
      live_count: partNetworking.live.summary.count_total,
      live_bytes: partNetworking.live.summary.byte_total,
      live_avg_dur: partNetworking.live.summary.average_dur,
      live_small_part_avg_dur:
        partNetworking.live.summary.small_part_average_dur,
      live_large_part_avg_dur:
        partNetworking.live.summary.large_part_average_dur,
      regular_count: partNetworking.regular.summary.count_total,
      regular_bytes: partNetworking.regular.summary.byte_total,
      regular_avg_dur: partNetworking.regular.summary.average_dur,
      regular_small_part_avg_dur:
        partNetworking.regular.summary.small_part_average_dur,
      regular_large_part_avg_dur:
        partNetworking.regular.summary.large_part_average_dur,
      node_restarted_in_period:
        partNetworking.live.warnings !== undefined ||
        partNetworking.regular.warnings !== undefined,
    });
  }

  return result;
}

function formatBytes(bytes: number): string {
  const prefixes = ["B", "KB", "MB", "GB", "TB", "PB"];
  const cutoffs = [0, 10 ** 3, 10 ** 6, 10 ** 9, 10 ** 12, 10 ** 15];

  for (let i = prefixes.length - 1; i >= 0; i--) {
    if (bytes > cutoffs[i]) {
      return `${(bytes / cutoffs[i]).toFixed(2)} ${prefixes[i]}`;
    }
  }

  return `${bytes} B`;
}

interface ProcessedPartNetworkingData {
  node: string;
  live_count: number;
  live_bytes: number;
  live_avg_dur: number;
  live_small_part_avg_dur: number;
  live_large_part_avg_dur: number;
  regular_count: number;
  regular_bytes: number;
  regular_avg_dur: number;
  regular_small_part_avg_dur: number;
  regular_large_part_avg_dur: number;
  node_restarted_in_period: boolean;
}

export default PartNetworking;
